import {
  Document, Page, Text, View, StyleSheet,
  Image as PDFImage,
} from '@react-pdf/renderer';

// ── Helpers ─────────────────────────────────────────────────────────────────
const fmtEur = (n) =>
  n == null ? '—' : `${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;

const fmtData = (d) => {
  if (!d) return '—';
  const [y, m, g] = d.split('-');
  return `${g}/${m}/${y}`;
};

// R1 — etichetta di fallback per riga scadenziario senza etichetta esplicita
const MODALITA_LABEL = {
  percentuale: 'Percentuale',
  importo_fisso: 'Importo fisso',
  resto: 'Saldo (resto)',
};

// R1 — descrizione modalità per la colonna "Modalità" (mostra la % come nel dettaglio)
const modalitaDescr = (rp) => {
  if (rp.modalita === 'percentuale') return `${parseFloat(rp.valore) || 0}% del totale`;
  if (rp.modalita === 'importo_fisso') return 'Importo fisso';
  if (rp.modalita === 'resto') return 'Saldo (resto)';
  return MODALITA_LABEL[rp.modalita] || '';
};

// Palette brand — coerente con CantierePDF.jsx
const GREEN  = '#2EA084';
const DARK   = '#1a1a2e';
const GRAY   = '#868e96';
const LIGHT  = '#f8f9fa';
const BORDER = '#e9ecef';

// Margini standard: 2.5 cm = ~71 pt (spazio carta intestata)
const MARGIN_TOP = 71;
const MARGIN_BOTTOM = 71;

// ── Stili ────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica', backgroundColor: '#fff', fontSize: 10, color: DARK,
    paddingTop: MARGIN_TOP, paddingBottom: MARGIN_BOTTOM, paddingLeft: 30, paddingRight: 30,
  },

  // Header
  headerBar: { height: 5, backgroundColor: GREEN },
  headerContent: {
    paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: BORDER, borderBottomStyle: 'solid',
  },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  logo: { width: 60, height: 60, objectFit: 'contain', marginBottom: 4 },
  aziendaNome: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 2 },
  aziendaInfo: { fontSize: 8, color: GRAY, lineHeight: 1.5 },

  // Titolo preventivo
  titleSection: {
    paddingTop: 14, paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: BORDER, borderBottomStyle: 'solid',
  },
  title: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 4 },
  titleSub: { fontSize: 9, color: GRAY, lineHeight: 1.6 },

  // Sezioni
  body: { paddingTop: 14 },
  sectionTitle: {
    fontSize: 8, fontFamily: 'Helvetica-Bold', color: GREEN,
    textTransform: 'uppercase', letterSpacing: 1.5,
    marginBottom: 8, marginTop: 14,
  },

  // Due colonne cliente/professionista
  twoCol: { flexDirection: 'row', gap: 20 },
  col: { flex: 1 },
  infoLabel: { fontSize: 7, color: GRAY, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 1 },
  infoValue: { fontSize: 9, color: DARK, marginBottom: 5 },

  // Tabella righe
  tableWrap: { borderWidth: 1, borderColor: BORDER, borderStyle: 'solid', borderRadius: 4, marginTop: 4 },
  tableHeader: {
    flexDirection: 'row', backgroundColor: LIGHT,
    paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
  },
  tableRow: {
    flexDirection: 'row',
    paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
    borderTopWidth: 1, borderTopColor: BORDER, borderTopStyle: 'solid',
  },
  tableHCell: { fontSize: 7, color: GRAY, textTransform: 'uppercase', fontFamily: 'Helvetica-Bold', letterSpacing: 0.4 },
  tableCell: { fontSize: 9, color: DARK },
  tableCellRight: { fontSize: 9, color: DARK, textAlign: 'right' },

  // Proporzioni colonne: Descrizione 50%, Qtà 10%, Prezzo 20%, Totale 20%
  colDesc:   { width: '50%' },
  colQta:    { width: '10%', textAlign: 'right' },
  colPrezzo: { width: '20%', textAlign: 'right' },
  colTotale: { width: '20%', textAlign: 'right' },

  // Totali
  totaliBox: { marginTop: 10, alignItems: 'flex-end' },
  totaliRow: { flexDirection: 'row', width: 220, justifyContent: 'space-between', marginBottom: 3 },
  totaliLabel: { fontSize: 9, color: GRAY },
  totaliValue: { fontSize: 9, color: DARK },
  totaliSep: { width: 220, height: 1, backgroundColor: BORDER, marginTop: 4, marginBottom: 4 },
  totaliTotLabel: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: DARK },
  totaliTotValue: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: DARK },

  // Condizioni pagamento
  condBox: {
    marginTop: 16, padding: 10, backgroundColor: LIGHT, borderRadius: 4,
  },
  condTitle: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: GREEN, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
  condText: { fontSize: 9, color: DARK, lineHeight: 1.5 },
  condNote: { fontSize: 9, color: GRAY, lineHeight: 1.5, marginTop: 4 },

  // Scadenziario pagamenti (R1)
  scadNote: { fontSize: 8, color: GRAY, marginTop: -2, marginBottom: 6 },

  // Footer firma
  firmaBox: { marginTop: 24, paddingTop: 14, borderTopWidth: 1, borderTopColor: BORDER, borderTopStyle: 'solid' },
  firmaText: { fontSize: 8, color: GRAY, lineHeight: 1.8 },
  firmaLine: { fontSize: 9, color: DARK, marginTop: 20 },

  // Numero pagina
  pageNum: {
    position: 'absolute', bottom: 24, left: 30, right: 30,
    textAlign: 'center', fontSize: 8, color: GRAY,
  },
});


// ── Componente ──────────────────────────────────────────────────────────────
export default function PreventivoPDF({ data }) {
  if (!data) return null;
  const { preventivo: p, righe, cliente, azienda, pagamenti } = data;

  // Logo: usare solo se URL http valido
  const logoSrc = azienda?.logo_url && azienda.logo_url.startsWith('http')
    ? azienda.logo_url : null;

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* ── Header: barra verde + dati azienda ── */}
        <View style={s.headerBar} />
        <View style={s.headerContent}>
          <View style={s.headerRow}>
            <View style={{ flex: 1 }}>
              <Text style={s.aziendaNome}>{azienda?.nome || 'Azienda'}</Text>
              <Text style={s.aziendaInfo}>
                {[azienda?.indirizzo, azienda?.citta, azienda?.provincia].filter(Boolean).join(', ')}
              </Text>
              {azienda?.piva ? <Text style={s.aziendaInfo}>P.IVA {azienda.piva}</Text> : null}
              <Text style={s.aziendaInfo}>
                {[azienda?.email, azienda?.telefono].filter(Boolean).join(' | ')}
              </Text>
            </View>
            {logoSrc && (
              <PDFImage src={logoSrc} style={s.logo} />
            )}
          </View>
        </View>

        {/* ── Titolo preventivo ── */}
        <View style={s.titleSection}>
          <Text style={s.title}>PREVENTIVO N. {p.numero}</Text>
          <Text style={s.titleSub}>
            Data: {fmtData(p.data_emissione)}{'    '}Valido fino al: {fmtData(p.data_scadenza)}
          </Text>
          {p.tipo_lavoro ? <Text style={s.titleSub}>Tipo lavoro: {p.tipo_lavoro}</Text> : null}
        </View>

        <View style={s.body}>

          {/* ── Due colonne: Cliente + Professionista ── */}
          <View style={s.twoCol}>
            <View style={s.col}>
              <Text style={s.sectionTitle}>Dati cliente</Text>
              {cliente ? (
                <>
                  <Text style={s.infoValue}>{cliente.nome}</Text>
                  {cliente.indirizzo ? <Text style={s.infoValue}>
                    {[cliente.indirizzo, cliente.cap, cliente.citta, cliente.provincia].filter(Boolean).join(', ')}
                  </Text> : null}
                  {cliente.codice_fiscale ? (
                    <><Text style={s.infoLabel}>Codice Fiscale</Text><Text style={s.infoValue}>{cliente.codice_fiscale}</Text></>
                  ) : null}
                  {cliente.piva ? (
                    <><Text style={s.infoLabel}>P.IVA</Text><Text style={s.infoValue}>{cliente.piva}</Text></>
                  ) : null}
                  {cliente.email || cliente.telefono ? (
                    <Text style={s.infoValue}>
                      {[cliente.email, cliente.telefono].filter(Boolean).join(' | ')}
                    </Text>
                  ) : null}
                </>
              ) : (
                <Text style={{ ...s.infoValue, color: GRAY }}>Nessun cliente selezionato</Text>
              )}
            </View>

            <View style={s.col}>
              <Text style={s.sectionTitle}>Professionista</Text>
              <Text style={s.infoValue}>{azienda?.nome}</Text>
              {azienda?.indirizzo ? <Text style={s.infoValue}>
                {[azienda.indirizzo, azienda.citta, azienda.provincia].filter(Boolean).join(', ')}
              </Text> : null}
              {azienda?.piva ? (
                <><Text style={s.infoLabel}>P.IVA</Text><Text style={s.infoValue}>{azienda.piva}</Text></>
              ) : null}
              <Text style={s.infoValue}>
                {[azienda?.email, azienda?.telefono].filter(Boolean).join(' | ')}
              </Text>
            </View>
          </View>

          {/* ── Tabella righe ── */}
          <Text style={s.sectionTitle}>Descrizione lavori</Text>
          <View style={s.tableWrap}>
            <View style={s.tableHeader}>
              <Text style={{ ...s.tableHCell, ...s.colDesc }}>Descrizione</Text>
              <Text style={{ ...s.tableHCell, ...s.colQta, textAlign: 'right' }}>Qtà</Text>
              <Text style={{ ...s.tableHCell, ...s.colPrezzo, textAlign: 'right' }}>Prezzo unit.</Text>
              <Text style={{ ...s.tableHCell, ...s.colTotale, textAlign: 'right' }}>Totale</Text>
            </View>
            {(righe || []).map((r, i) => (
              <View key={i} style={s.tableRow}>
                <Text style={{ ...s.tableCell, ...s.colDesc }}>{r.descrizione}</Text>
                <Text style={{ ...s.tableCellRight, ...s.colQta }}>{r.quantita}</Text>
                <Text style={{ ...s.tableCellRight, ...s.colPrezzo }}>{parseFloat(r.prezzo_unitario) > 0 ? fmtEur(r.prezzo_unitario) : '—'}</Text>
                <Text style={{ ...s.tableCellRight, ...s.colTotale }}>{parseFloat(r.totale_riga) > 0 ? fmtEur(r.totale_riga) : '—'}</Text>
              </View>
            ))}
          </View>

          {/* ── Totali ── */}
          <View style={s.totaliBox}>
            {p.sconto_percentuale ? (
              <>
                <View style={s.totaliRow}>
                  <Text style={s.totaliLabel}>Subtotale</Text>
                  <Text style={s.totaliValue}>{fmtEur(p.imponibile_lordo)}</Text>
                </View>
                <View style={s.totaliRow}>
                  <Text style={s.totaliLabel}>Sconto {p.sconto_percentuale}%</Text>
                  <Text style={s.totaliValue}>-{fmtEur(p.sconto_importo)}</Text>
                </View>
              </>
            ) : null}
            <View style={s.totaliRow}>
              <Text style={s.totaliLabel}>Imponibile</Text>
              <Text style={s.totaliValue}>{fmtEur(p.imponibile)}</Text>
            </View>
            <View style={s.totaliRow}>
              <Text style={s.totaliLabel}>IVA ({p.aliquota_iva}%)</Text>
              <Text style={s.totaliValue}>{fmtEur(p.iva_totale)}</Text>
            </View>
            <View style={s.totaliSep} />
            <View style={s.totaliRow}>
              <Text style={s.totaliTotLabel}>TOTALE</Text>
              <Text style={s.totaliTotValue}>{fmtEur(p.totale)}</Text>
            </View>
          </View>

          {/* ── Scadenziario pagamenti (R1) — dati da /pagamenti/ scadenziario ── */}
          {pagamenti && pagamenti.righe && pagamenti.righe.length > 0 && (
            <>
              <Text style={s.sectionTitle}>Scadenziario pagamenti</Text>
              <Text style={s.scadNote}>
                Importi calcolati sul totale ({fmtEur(pagamenti.base_totale_lordo)}) — IVA inclusa
              </Text>
              <View style={s.tableWrap}>
                <View style={s.tableHeader}>
                  <Text style={{ ...s.tableHCell, width: '48%' }}>Voce</Text>
                  <Text style={{ ...s.tableHCell, width: '32%' }}>Modalità</Text>
                  <Text style={{ ...s.tableHCell, width: '20%', textAlign: 'right' }}>Importo</Text>
                </View>
                {pagamenti.righe.map((rp, i) => (
                  <View key={rp.id || i} style={s.tableRow}>
                    <Text style={{ ...s.tableCell, width: '48%' }}>
                      {rp.etichetta || MODALITA_LABEL[rp.modalita] || rp.modalita}
                    </Text>
                    <Text style={{ ...s.tableCell, width: '32%' }}>{modalitaDescr(rp)}</Text>
                    <Text style={{ ...s.tableCellRight, width: '20%' }}>{fmtEur(rp.importo)}</Text>
                  </View>
                ))}
              </View>
            </>
          )}

          {/* ── Condizioni di pagamento ── */}
          <View style={s.condBox}>
            <Text style={s.condTitle}>Condizioni di pagamento</Text>
            <Text style={s.condText}>Modalità: {p.modalita_pagamento}</Text>
            {p.note_cliente ? <Text style={s.condNote}>{p.note_cliente}</Text> : null}
          </View>

          {/* ── Footer firma ── */}
          <View style={s.firmaBox}>
            <Text style={s.firmaText}>
              Il presente preventivo ha validità fino al {fmtData(p.data_scadenza)}.{'\n'}
              Per accettazione, firmare e restituire copia firmata.
            </Text>
            <Text style={s.firmaLine}>Firma cliente: _________________________________</Text>
            <Text style={s.firmaLine}>Data: _________________________________</Text>
          </View>

        </View>

        {/* Numero pagina */}
        <Text style={s.pageNum} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />

      </Page>
    </Document>
  );
}
